import React from 'react';
import clsx from 'clsx';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';

import Button from '@material-ui/core/Button';
import BackgroundButton from '../Buttons/BackgroundButton/BackgroundButton';
import EndCallButton from '../Buttons/EndCallButton/EndCallButton';
import { isMobile } from '../../utils';
import Menu from './Menu/Menu';
import useParticipants from '../../hooks/useParticipants/useParticipants';
import useRoomState from '../../hooks/useRoomState/useRoomState';
import useVideoContext from '../../hooks/useVideoContext/useVideoContext';
import { Typography, Grid, Hidden } from '@material-ui/core';
import ToggleAudioButton from '../Buttons/ToggleAudioButton/ToggleAudioButton';
import ToggleVideoButton from '../Buttons/ToggleVideoButton/ToggleVideoButton';
import ToggleScreenShareButton from '../Buttons/ToogleScreenShareButton/ToggleScreenShareButton';

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		container: {
			backgroundColor: '#FFFFFF',
			borderTop: `1px solid ${theme.palette.divider}`,
			bottom: 0,
			left: 0,
			right: 0,
			height: `${theme.footerHeight}px`,
			position: 'fixed',
			display: 'flex',
			padding: '0 20px',
			boxSizing: 'border-box',
			zIndex: 10,
			[theme.breakpoints.down('sm')]: {
				height: `calc(${theme.mobileFooterHeight}px + env(safe-area-inset-bottom))`,
				padding: '0 4px env(safe-area-inset-bottom)',
			},
		},
		bar: {
			width: '100%',
			height: '100%',
		},
		side: {
			flex: 1,
			minWidth: 0,
		},
		participantCount: {
			color: theme.palette.text.secondary,
			whiteSpace: 'nowrap',
		},
		controls: {
			display: 'flex',
			alignItems: 'center',
			justifyContent: 'center',
			gap: 8,
			[theme.breakpoints.down('sm')]: {
				width: '100%',
				gap: 2,
			},
		},
		barButton: {
			minHeight: 44,
			padding: '8px 14px',
			whiteSpace: 'nowrap',
			[theme.breakpoints.down('sm')]: {
				flex: '1 1 0',
				minWidth: 0,
				minHeight: 56,
				padding: '6px 2px',
				fontSize: '0.75rem',
				lineHeight: 1.15,
				whiteSpace: 'normal',
				'& .MuiButton-label': {
					flexDirection: 'column',
					gap: 2,
				},
				'& .MuiButton-startIcon, & .MuiButton-endIcon': {
					margin: 0,
				},
			},
		},
		leaveButton: {
			marginLeft: 12,
			[theme.breakpoints.down('sm')]: {
				marginLeft: 2,
			},
		},
		screenShareBanner: {
			position: 'fixed',
			zIndex: 8,
			bottom: `${theme.footerHeight}px`,
			left: 0,
			right: 0,
			height: '104px',
			background: 'rgba(0, 0, 0, 0.6)',
			'& h6': {
				color: 'white',
			},
			'& button': {
				background: 'white',
				color: theme.brand,
				border: `2px solid ${theme.brand}`,
				margin: '0 2em',
				'&:hover': {
					color: '#A8231B',
					border: `2px solid #A8231B`,
					background: '#FFE9E7',
				},
			},
		},
	}),
);

export default function MenuBar() {
	const classes = useStyles();
	const { isSharingScreen, toggleScreenShare } = useVideoContext();
	const roomState = useRoomState();
	const isReconnecting = roomState === 'reconnecting';
	const participants = useParticipants();

	return (
		<>
			{isSharingScreen && (
				<Grid
					container
					justifyContent="center"
					alignItems="center"
					className={classes.screenShareBanner}
				>
					<Typography variant="h6">
						You are sharing your screen
					</Typography>
					<Button onClick={() => toggleScreenShare()}>
						Stop Sharing
					</Button>
				</Grid>
			)}
			<footer className={classes.container}>
				<Grid
					container
					justifyContent="space-between"
					alignItems="center"
					wrap="nowrap"
					className={classes.bar}
				>
					<Hidden smDown>
						<Grid className={classes.side}>
							<Typography
								variant="body1"
								className={classes.participantCount}
							>
								{participants.length + 1} participant
								{participants.length ? 's' : ''}
							</Typography>
						</Grid>
					</Hidden>
					<Grid item className={classes.controls}>
						<ToggleAudioButton
							className={classes.barButton}
							disabled={isReconnecting}
						/>
						<ToggleVideoButton
							className={classes.barButton}
							disabled={isReconnecting}
						/>
						{!isSharingScreen && !isMobile && (
							<Hidden smDown>
								<ToggleScreenShareButton
									className={classes.barButton}
									disabled={isReconnecting}
								/>
							</Hidden>
						)}
						<BackgroundButton
							className={classes.barButton}
							disabled={isReconnecting}
						/>
						<Menu buttonClassName={classes.barButton} />
						<EndCallButton
							className={clsx(
								classes.barButton,
								classes.leaveButton,
							)}
						/>
					</Grid>
					<Hidden smDown>
						<Grid className={classes.side} />
					</Hidden>
				</Grid>
			</footer>
		</>
	);
}
